import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const PlanContext = createContext(null);

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) throw new Error('usePlan must be used within PlanProvider');
  return context;
};

export const PlanProvider = ({ children }) => {
  const { api, user } = useAuth();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPlanFeatures();
    } else {
      setPlanData(null);
      setLoading(false);
    }
  }, [user]);

  const fetchPlanFeatures = async () => {
    try {
      const res = await api.get('/auth/plan-features');
      setPlanData(res.data);
    } catch (err) {
      console.error('Failed to fetch plan features:', err);
      // Default to free plan features
      setPlanData({
        plan: 'free',
        effective_plan: 'free',
        is_trial: false,
        trial_days_left: 0,
        features: {
          dark_mode: false,
          email_sending: false,
          csv_export: false,
          excel_export: false,
          btw_export: false,
          client_portal: false,
          credit_notes: false,
          auto_reminders: false,
          all_templates: false,
          km_calculator: true,
          pdf_export: true
        },
        limits: {
          time_entries: 5,
          mileage_entries: 5,
          clients: 1,
          invoices: 1,
          projects: 0,
          reminders: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const hasFeature = (feature) => {
    if (!planData) return false;
    return planData.features[feature] || false;
  };

  const getLimit = (resource) => {
    if (!planData) return 0;
    const limit = planData.limits[resource];
    return limit === Infinity ? '∞' : limit;
  };

  const isPro = () => {
    return planData?.effective_plan === 'pro';
  };

  const isBasicOrHigher = () => {
    return planData?.effective_plan === 'basic' || planData?.effective_plan === 'pro';
  };

  const refreshPlanData = () => {
    fetchPlanFeatures();
  };

  return (
    <PlanContext.Provider value={{
      planData,
      loading,
      hasFeature,
      getLimit,
      isPro,
      isBasicOrHigher,
      refreshPlanData
    }}>
      {children}
    </PlanContext.Provider>
  );
};

export default PlanContext;
